import { normalizeLocale, type SupportedLocale } from './config';

export type CameraCaptureCopy = {
  cameraCaptureFailed: string;
  cameraTooManyFiles: string;
  cameraCaptureAnswer: string;
  cameraUploadFailed: string;
  cameraPermissionDenied: string;
  cameraUnavailable: string;
  cameraStartFailed: string;
  cameraPermissionPrompt: string;
  cameraCollapse: string;
  cameraExpand: string;
  cameraStepOf: string;
  cameraEnable: string;
  cameraStarting: string;
  cameraTurnOff: string;
  cameraRetake: string;
  cameraTakePhoto: string;
  cameraLowResolution: string;
  cameraRequiredOneRemaining: string;
  cameraRequiredRemaining: string;
  cameraReady: string;
  cameraUsePhotos: string;
};

const cameraCaptureCopy: Record<SupportedLocale, CameraCaptureCopy> = {
  en: {
    cameraCaptureFailed:
      'The photo could not be captured. Try again or attach a file.',
    cameraTooManyFiles: 'Only the available capture steps were filled.',
    cameraCaptureAnswer: '{count} guided photos provided',
    cameraUploadFailed: 'The photos could not be uploaded. Please try again.',
    cameraPermissionDenied:
      'Camera access was denied. You can attach photos instead.',
    cameraUnavailable: 'No camera is available. You can attach photos instead.',
    cameraStartFailed:
      'The camera could not start. You can attach photos instead.',
    cameraPermissionPrompt:
      'Enable the camera when you are ready. Nothing is uploaded until you approve the photos.',
    cameraCollapse: 'Collapse camera',
    cameraExpand: 'Expand camera',
    cameraStepOf: '{current} of {total}',
    cameraEnable: 'Enable camera',
    cameraStarting: 'Starting camera…',
    cameraTurnOff: 'Turn off camera',
    cameraRetake: 'Retake',
    cameraTakePhoto: 'Take photo',
    cameraLowResolution:
      'One or more photos are low resolution. You can retake them or continue.',
    cameraRequiredOneRemaining: '1 required photo remaining',
    cameraRequiredRemaining: '{count} required photos remaining',
    cameraReady: '{count} photos ready',
    cameraUsePhotos: 'Use these photos',
  },
  'pt-BR': {
    cameraCaptureFailed:
      'A foto n\u00E3o p\u00F4de ser capturada. Tente de novo ou prenda um arquivo.',
    cameraTooManyFiles:
      'Apenas os passos de captura dispon\u00EDveis foram preenchidos.',
    cameraCaptureAnswer: '{count} fotos guiadas fornecidas',
    cameraUploadFailed:
      'As fotos n\u00E3o puderam ser enviadas. Por favor, tente de novo.',
    cameraPermissionDenied:
      'O acesso da c\u00E2mera foi negado. Voc\u00EA pode anexar fotos em vez disso.',
    cameraUnavailable:
      'Nenhuma c\u00E2mera est\u00E1 dispon\u00EDvel. Voc\u00EA pode anexar fotos em vez disso.',
    cameraStartFailed:
      'A c\u00E2mera n\u00E3o podia ligar. Voc\u00EA pode anexar fotos em vez disso.',
    cameraPermissionPrompt:
      'Active a c\u00E2mera quando estiver pronto. Nada \u00E9 carregado at\u00E9 voc\u00EA aprovar as fotos.',
    cameraCollapse: 'Recolher c\u00E2mera',
    cameraExpand: 'Expandir a c\u00E2mera',
    cameraStepOf: '{current} de {total}',
    cameraEnable: 'Activar a c\u00E2mara',
    cameraStarting: 'Iniciando c\u00E2mera...',
    cameraTurnOff: 'Desligue a c\u00E2mera.',
    cameraRetake: 'Retomar',
    cameraTakePhoto: 'Tire uma foto.',
    cameraLowResolution:
      'Uma ou mais fotos s\u00E3o de baixa resolu\u00E7\u00E3o. Voc\u00EA pode recuper\u00E1-los ou continuar.',
    cameraRequiredOneRemaining: 'Falta uma foto.',
    cameraRequiredRemaining: '{count} fotos necess\u00E1rias restantes.',
    cameraReady: '{count} fotos prontas.',
    cameraUsePhotos: 'Use estas fotos.',
  },
  ja: {
    cameraCaptureFailed:
      '写真を撮影できませんでした。再試行するか、ファイルを添付してください。',
    cameraTooManyFiles: '空いている撮影枠のみ入力されました。',
    cameraCaptureAnswer: 'ガイド付き写真を{count}枚提供しました',
    cameraUploadFailed:
      '写真をアップロードできませんでした。もう一度お試しください。',
    cameraPermissionDenied:
      'カメラへのアクセスが拒否されました。代わりに写真を添付できます。',
    cameraUnavailable:
      '利用できるカメラがありません。代わりに写真を添付できます。',
    cameraStartFailed:
      'カメラを起動できませんでした。代わりに写真を添付できます。',
    cameraPermissionPrompt:
      '準備ができたらカメラを有効にしてください。承認するまで写真はアップロードされません。',
    cameraCollapse: 'カメラを折りたたむ',
    cameraExpand: 'カメラを拡大する',
    cameraStepOf: '{total}件中{current}件目',
    cameraEnable: 'カメラを有効にする',
    cameraStarting: 'カメラを起動中…',
    cameraTurnOff: 'カメラをオフにする',
    cameraRetake: '撮り直す',
    cameraTakePhoto: '撮影する',
    cameraLowResolution:
      '解像度が低い写真があります。撮り直すか、そのまま続行できます。',
    cameraRequiredOneRemaining: '必須写真があと1枚あります',
    cameraRequiredRemaining: '必須写真があと{count}枚あります',
    cameraReady: '{count}枚の写真を使用できます',
    cameraUsePhotos: 'この写真を使用',
  },
  ar: {
    cameraCaptureFailed: 'تعذر التقاط الصورة. حاول مرة أخرى أو أرفق ملفًا.',
    cameraTooManyFiles: 'تم ملء خطوات الالتقاط المتاحة فقط.',
    cameraCaptureAnswer: 'تم تقديم {count} صور إرشادية',
    cameraUploadFailed: 'تعذر رفع الصور. يرجى المحاولة مرة أخرى.',
    cameraPermissionDenied:
      'تم رفض الوصول إلى الكاميرا. يمكنك إرفاق الصور بدلًا من ذلك.',
    cameraUnavailable: 'لا توجد كاميرا متاحة. يمكنك إرفاق الصور بدلًا من ذلك.',
    cameraStartFailed: 'تعذر تشغيل الكاميرا. يمكنك إرفاق الصور بدلًا من ذلك.',
    cameraPermissionPrompt:
      'فعّل الكاميرا عندما تكون مستعدًا. لن يتم رفع أي صورة حتى توافق عليها.',
    cameraCollapse: 'طي الكاميرا',
    cameraExpand: 'توسيع الكاميرا',
    cameraStepOf: '{current} من {total}',
    cameraEnable: 'تفعيل الكاميرا',
    cameraStarting: 'جارٍ تشغيل الكاميرا…',
    cameraTurnOff: 'إيقاف الكاميرا',
    cameraRetake: 'إعادة الالتقاط',
    cameraTakePhoto: 'التقاط صورة',
    cameraLowResolution:
      'دقة صورة واحدة أو أكثر منخفضة. يمكنك إعادة التقاطها أو المتابعة.',
    cameraRequiredOneRemaining: 'تبقت صورة مطلوبة واحدة',
    cameraRequiredRemaining: 'تبقى {count} صور مطلوبة',
    cameraReady: '{count} صور جاهزة',
    cameraUsePhotos: 'استخدام هذه الصور',
  },
  de: {
    cameraCaptureFailed:
      'Das Foto konnte nicht aufgenommen werden. Versuche es erneut oder hänge eine Datei an.',
    cameraTooManyFiles:
      'Es wurden nur die verfügbaren Aufnahmeschritte befüllt.',
    cameraCaptureAnswer: '{count} geführte Fotos bereitgestellt',
    cameraUploadFailed:
      'Die Fotos konnten nicht hochgeladen werden. Bitte versuche es erneut.',
    cameraPermissionDenied:
      'Der Kamerazugriff wurde verweigert. Du kannst stattdessen Fotos anhängen.',
    cameraUnavailable:
      'Keine Kamera verfügbar. Du kannst stattdessen Fotos anhängen.',
    cameraStartFailed:
      'Die Kamera konnte nicht gestartet werden. Du kannst stattdessen Fotos anhängen.',
    cameraPermissionPrompt:
      'Aktiviere die Kamera, wenn du bereit bist. Vor deiner Freigabe wird nichts hochgeladen.',
    cameraCollapse: 'Kamera einklappen',
    cameraExpand: 'Kamera vergrößern',
    cameraStepOf: '{current} von {total}',
    cameraEnable: 'Kamera aktivieren',
    cameraStarting: 'Kamera wird gestartet…',
    cameraTurnOff: 'Kamera ausschalten',
    cameraRetake: 'Neu aufnehmen',
    cameraTakePhoto: 'Foto aufnehmen',
    cameraLowResolution:
      'Mindestens ein Foto hat eine niedrige Auflösung. Du kannst es neu aufnehmen oder fortfahren.',
    cameraRequiredOneRemaining: 'Noch 1 Pflichtfoto',
    cameraRequiredRemaining: 'Noch {count} Pflichtfotos',
    cameraReady: '{count} Fotos bereit',
    cameraUsePhotos: 'Diese Fotos verwenden',
  },
  es: {
    cameraCaptureFailed:
      'No se pudo tomar la foto. Inténtalo de nuevo o adjunta un archivo.',
    cameraTooManyFiles: 'Solo se completaron los pasos de captura disponibles.',
    cameraCaptureAnswer: 'Se proporcionaron {count} fotos guiadas',
    cameraUploadFailed: 'No se pudieron subir las fotos. Inténtalo de nuevo.',
    cameraPermissionDenied:
      'Se denegó el acceso a la cámara. Puedes adjuntar fotos en su lugar.',
    cameraUnavailable:
      'No hay ninguna cámara disponible. Puedes adjuntar fotos en su lugar.',
    cameraStartFailed:
      'No se pudo iniciar la cámara. Puedes adjuntar fotos en su lugar.',
    cameraPermissionPrompt:
      'Activa la cámara cuando estés listo. No se subirá nada hasta que apruebes las fotos.',
    cameraCollapse: 'Contraer cámara',
    cameraExpand: 'Ampliar cámara',
    cameraStepOf: '{current} de {total}',
    cameraEnable: 'Activar cámara',
    cameraStarting: 'Iniciando cámara…',
    cameraTurnOff: 'Apagar cámara',
    cameraRetake: 'Volver a tomar',
    cameraTakePhoto: 'Tomar foto',
    cameraLowResolution:
      'Una o más fotos tienen baja resolución. Puedes repetirlas o continuar.',
    cameraRequiredOneRemaining: 'Queda 1 foto obligatoria',
    cameraRequiredRemaining: 'Quedan {count} fotos obligatorias',
    cameraReady: '{count} fotos listas',
    cameraUsePhotos: 'Usar estas fotos',
  },
  fr: {
    cameraCaptureFailed:
      "La photo n'a pas pu être prise. Réessayez ou joignez un fichier.",
    cameraTooManyFiles:
      'Seules les étapes de capture disponibles ont été remplies.',
    cameraCaptureAnswer: '{count} photos guidées fournies',
    cameraUploadFailed:
      "Les photos n'ont pas pu être importées. Veuillez réessayer.",
    cameraPermissionDenied:
      "L'accès à la caméra a été refusé. Vous pouvez joindre des photos à la place.",
    cameraUnavailable:
      "Aucune caméra n'est disponible. Vous pouvez joindre des photos à la place.",
    cameraStartFailed:
      "La caméra n'a pas pu démarrer. Vous pouvez joindre des photos à la place.",
    cameraPermissionPrompt:
      'Activez la caméra lorsque vous êtes prêt. Rien ne sera importé avant votre validation.',
    cameraCollapse: 'Réduire la caméra',
    cameraExpand: 'Agrandir la caméra',
    cameraStepOf: '{current} sur {total}',
    cameraEnable: 'Activer la caméra',
    cameraStarting: 'Démarrage de la caméra…',
    cameraTurnOff: 'Éteindre la caméra',
    cameraRetake: 'Reprendre',
    cameraTakePhoto: 'Prendre une photo',
    cameraLowResolution:
      'Une ou plusieurs photos ont une faible résolution. Vous pouvez les reprendre ou continuer.',
    cameraRequiredOneRemaining: 'Il reste 1 photo requise',
    cameraRequiredRemaining: '{count} photos requises restantes',
    cameraReady: '{count} photos prêtes',
    cameraUsePhotos: 'Utiliser ces photos',
  },
  hi: {
    cameraCaptureFailed:
      'फ़ोटो नहीं ली जा सकी। फिर कोशिश करें या फ़ाइल संलग्न करें।',
    cameraTooManyFiles: 'केवल उपलब्ध कैप्चर चरण भरे गए।',
    cameraCaptureAnswer: '{count} निर्देशित फ़ोटो दी गईं',
    cameraUploadFailed: 'फ़ोटो अपलोड नहीं हो सकीं। कृपया फिर कोशिश करें।',
    cameraPermissionDenied:
      'कैमरे की अनुमति नहीं मिली। आप इसके बजाय फ़ोटो संलग्न कर सकते हैं।',
    cameraUnavailable:
      'कोई कैमरा उपलब्ध नहीं है। आप इसके बजाय फ़ोटो संलग्न कर सकते हैं।',
    cameraStartFailed:
      'कैमरा शुरू नहीं हो सका। आप इसके बजाय फ़ोटो संलग्न कर सकते हैं।',
    cameraPermissionPrompt:
      'तैयार होने पर कैमरा चालू करें। आपकी स्वीकृति से पहले कुछ भी अपलोड नहीं होगा।',
    cameraCollapse: 'कैमरा छोटा करें',
    cameraExpand: 'कैमरा बड़ा करें',
    cameraStepOf: '{total} में से {current}',
    cameraEnable: 'कैमरा चालू करें',
    cameraStarting: 'कैमरा शुरू हो रहा है…',
    cameraTurnOff: 'कैमरा बंद करें',
    cameraRetake: 'फिर लें',
    cameraTakePhoto: 'फ़ोटो लें',
    cameraLowResolution:
      'एक या अधिक फ़ोटो कम रिज़ॉल्यूशन की हैं। आप उन्हें फिर ले सकते हैं या आगे बढ़ सकते हैं।',
    cameraRequiredOneRemaining: '1 आवश्यक फ़ोटो बाकी',
    cameraRequiredRemaining: '{count} आवश्यक फ़ोटो बाकी',
    cameraReady: '{count} फ़ोटो तैयार',
    cameraUsePhotos: 'इन फ़ोटो का उपयोग करें',
  },
  id: {
    cameraCaptureFailed:
      'Foto tidak dapat diambil. Coba lagi atau lampirkan file.',
    cameraTooManyFiles: 'Hanya langkah pengambilan yang tersedia yang diisi.',
    cameraCaptureAnswer: '{count} foto terpandu diberikan',
    cameraUploadFailed: 'Foto tidak dapat diunggah. Silakan coba lagi.',
    cameraPermissionDenied:
      'Akses kamera ditolak. Anda dapat melampirkan foto sebagai gantinya.',
    cameraUnavailable:
      'Tidak ada kamera yang tersedia. Anda dapat melampirkan foto sebagai gantinya.',
    cameraStartFailed:
      'Kamera tidak dapat dimulai. Anda dapat melampirkan foto sebagai gantinya.',
    cameraPermissionPrompt:
      'Aktifkan kamera saat Anda siap. Tidak ada yang diunggah sebelum Anda menyetujui foto.',
    cameraCollapse: 'Ciutkan kamera',
    cameraExpand: 'Perbesar kamera',
    cameraStepOf: '{current} dari {total}',
    cameraEnable: 'Aktifkan kamera',
    cameraStarting: 'Memulai kamera…',
    cameraTurnOff: 'Matikan kamera',
    cameraRetake: 'Ambil ulang',
    cameraTakePhoto: 'Ambil foto',
    cameraLowResolution:
      'Satu atau beberapa foto beresolusi rendah. Anda dapat mengambil ulang atau melanjutkan.',
    cameraRequiredOneRemaining: '1 foto wajib tersisa',
    cameraRequiredRemaining: '{count} foto wajib tersisa',
    cameraReady: '{count} foto siap',
    cameraUsePhotos: 'Gunakan foto ini',
  },
  it: {
    cameraCaptureFailed:
      'Non è stato possibile scattare la foto. Riprova o allega un file.',
    cameraTooManyFiles:
      'Sono stati compilati solo i passaggi di acquisizione disponibili.',
    cameraCaptureAnswer: '{count} foto guidate fornite',
    cameraUploadFailed: 'Non è stato possibile caricare le foto. Riprova.',
    cameraPermissionDenied:
      "L'accesso alla fotocamera è stato negato. Puoi allegare le foto.",
    cameraUnavailable: 'Nessuna fotocamera disponibile. Puoi allegare le foto.',
    cameraStartFailed:
      'Non è stato possibile avviare la fotocamera. Puoi allegare le foto.',
    cameraPermissionPrompt:
      'Attiva la fotocamera quando sei pronto. Nulla verrà caricato prima della tua approvazione.',
    cameraCollapse: 'Riduci fotocamera',
    cameraExpand: 'Espandi fotocamera',
    cameraStepOf: '{current} di {total}',
    cameraEnable: 'Attiva fotocamera',
    cameraStarting: 'Avvio fotocamera…',
    cameraTurnOff: 'Spegni fotocamera',
    cameraRetake: 'Ripeti',
    cameraTakePhoto: 'Scatta foto',
    cameraLowResolution:
      'Una o più foto hanno una risoluzione bassa. Puoi ripeterle o continuare.',
    cameraRequiredOneRemaining: 'Rimane 1 foto obbligatoria',
    cameraRequiredRemaining: '{count} foto obbligatorie rimanenti',
    cameraReady: '{count} foto pronte',
    cameraUsePhotos: 'Usa queste foto',
  },
  ko: {
    cameraCaptureFailed:
      '사진을 촬영하지 못했습니다. 다시 시도하거나 파일을 첨부하세요.',
    cameraTooManyFiles: '사용 가능한 촬영 단계만 채웠습니다.',
    cameraCaptureAnswer: '가이드 사진 {count}장을 제공했습니다',
    cameraUploadFailed: '사진을 업로드하지 못했습니다. 다시 시도하세요.',
    cameraPermissionDenied:
      '카메라 접근이 거부되었습니다. 대신 사진을 첨부할 수 있습니다.',
    cameraUnavailable:
      '사용 가능한 카메라가 없습니다. 대신 사진을 첨부할 수 있습니다.',
    cameraStartFailed:
      '카메라를 시작하지 못했습니다. 대신 사진을 첨부할 수 있습니다.',
    cameraPermissionPrompt:
      '준비되면 카메라를 활성화하세요. 사진을 승인하기 전에는 업로드되지 않습니다.',
    cameraCollapse: '카메라 접기',
    cameraExpand: '카메라 확대',
    cameraStepOf: '{total}개 중 {current}개',
    cameraEnable: '카메라 활성화',
    cameraStarting: '카메라 시작 중…',
    cameraTurnOff: '카메라 끄기',
    cameraRetake: '다시 촬영',
    cameraTakePhoto: '사진 촬영',
    cameraLowResolution:
      '하나 이상의 사진 해상도가 낮습니다. 다시 촬영하거나 계속할 수 있습니다.',
    cameraRequiredOneRemaining: '필수 사진 1장 남음',
    cameraRequiredRemaining: '필수 사진 {count}장 남음',
    cameraReady: '사진 {count}장 준비됨',
    cameraUsePhotos: '이 사진 사용',
  },
  ru: {
    cameraCaptureFailed:
      'Не удалось сделать фото. Повторите попытку или прикрепите файл.',
    cameraTooManyFiles: 'Заполнены только доступные этапы съёмки.',
    cameraCaptureAnswer: 'Предоставлено фото: {count}',
    cameraUploadFailed: 'Не удалось загрузить фотографии. Повторите попытку.',
    cameraPermissionDenied:
      'Доступ к камере запрещён. Вместо этого можно прикрепить фотографии.',
    cameraUnavailable:
      'Камера недоступна. Вместо этого можно прикрепить фотографии.',
    cameraStartFailed:
      'Не удалось запустить камеру. Вместо этого можно прикрепить фотографии.',
    cameraPermissionPrompt:
      'Включите камеру, когда будете готовы. Ничего не будет загружено до вашего подтверждения.',
    cameraCollapse: 'Свернуть камеру',
    cameraExpand: 'Развернуть камеру',
    cameraStepOf: '{current} из {total}',
    cameraEnable: 'Включить камеру',
    cameraStarting: 'Камера запускается…',
    cameraTurnOff: 'Выключить камеру',
    cameraRetake: 'Переснять',
    cameraTakePhoto: 'Сделать фото',
    cameraLowResolution:
      'У одной или нескольких фотографий низкое разрешение. Их можно переснять или продолжить.',
    cameraRequiredOneRemaining: 'Осталась 1 обязательная фотография',
    cameraRequiredRemaining: 'Осталось обязательных фото: {count}',
    cameraReady: 'Готово фото: {count}',
    cameraUsePhotos: 'Использовать эти фото',
  },
  vi: {
    cameraCaptureFailed: 'Không thể chụp ảnh. Hãy thử lại hoặc đính kèm tệp.',
    cameraTooManyFiles: 'Chỉ các bước chụp còn trống được điền.',
    cameraCaptureAnswer: 'Đã cung cấp {count} ảnh có hướng dẫn',
    cameraUploadFailed: 'Không thể tải ảnh lên. Vui lòng thử lại.',
    cameraPermissionDenied:
      'Quyền truy cập máy ảnh bị từ chối. Bạn có thể đính kèm ảnh thay thế.',
    cameraUnavailable:
      'Không có máy ảnh khả dụng. Bạn có thể đính kèm ảnh thay thế.',
    cameraStartFailed:
      'Không thể khởi động máy ảnh. Bạn có thể đính kèm ảnh thay thế.',
    cameraPermissionPrompt:
      'Bật máy ảnh khi bạn sẵn sàng. Ảnh sẽ không được tải lên cho đến khi bạn xác nhận.',
    cameraCollapse: 'Thu gọn máy ảnh',
    cameraExpand: 'Mở rộng máy ảnh',
    cameraStepOf: '{current} / {total}',
    cameraEnable: 'Bật máy ảnh',
    cameraStarting: 'Đang bật máy ảnh…',
    cameraTurnOff: 'Tắt máy ảnh',
    cameraRetake: 'Chụp lại',
    cameraTakePhoto: 'Chụp ảnh',
    cameraLowResolution:
      'Một hoặc nhiều ảnh có độ phân giải thấp. Bạn có thể chụp lại hoặc tiếp tục.',
    cameraRequiredOneRemaining: 'Còn 1 ảnh bắt buộc',
    cameraRequiredRemaining: 'Còn {count} ảnh bắt buộc',
    cameraReady: '{count} ảnh đã sẵn sàng',
    cameraUsePhotos: 'Dùng các ảnh này',
  },
  zh: {
    cameraCaptureFailed: '无法拍摄照片。请重试或附加文件。',
    cameraTooManyFiles: '仅填充了可用的拍摄步骤。',
    cameraCaptureAnswer: '已提供 {count} 张引导照片',
    cameraUploadFailed: '无法上传照片。请重试。',
    cameraPermissionDenied: '相机访问被拒绝。你可以改为附加照片。',
    cameraUnavailable: '没有可用的相机。你可以改为附加照片。',
    cameraStartFailed: '无法启动相机。你可以改为附加照片。',
    cameraPermissionPrompt: '准备好后启用相机。照片在你确认之前不会上传。',
    cameraCollapse: '收起相机',
    cameraExpand: '展开相机',
    cameraStepOf: '第 {current} 项，共 {total} 项',
    cameraEnable: '启用相机',
    cameraStarting: '正在启动相机…',
    cameraTurnOff: '关闭相机',
    cameraRetake: '重新拍摄',
    cameraTakePhoto: '拍照',
    cameraLowResolution: '一张或多张照片分辨率较低。你可以重新拍摄或继续。',
    cameraRequiredOneRemaining: '还需 1 张必拍照片',
    cameraRequiredRemaining: '还需 {count} 张必拍照片',
    cameraReady: '{count} 张照片已就绪',
    cameraUsePhotos: '使用这些照片',
  },
  'zh-TW': {
    cameraCaptureFailed: '無法拍攝照片。請重試或附加檔案。',
    cameraTooManyFiles: '僅填入了可用的拍攝步驟。',
    cameraCaptureAnswer: '已提供 {count} 張引導照片',
    cameraUploadFailed: '無法上傳照片。請重試。',
    cameraPermissionDenied: '相機存取遭到拒絕。你可以改為附加照片。',
    cameraUnavailable: '沒有可用的相機。你可以改為附加照片。',
    cameraStartFailed: '無法啟動相機。你可以改為附加照片。',
    cameraPermissionPrompt: '準備好後啟用相機。照片在你確認之前不會上傳。',
    cameraCollapse: '收合相機',
    cameraExpand: '展開相機',
    cameraStepOf: '第 {current} 項，共 {total} 項',
    cameraEnable: '啟用相機',
    cameraStarting: '正在啟動相機…',
    cameraTurnOff: '關閉相機',
    cameraRetake: '重新拍攝',
    cameraTakePhoto: '拍照',
    cameraLowResolution: '一張或多張照片的解析度較低。你可以重新拍攝或繼續。',
    cameraRequiredOneRemaining: '還需 1 張必拍照片',
    cameraRequiredRemaining: '還需 {count} 張必拍照片',
    cameraReady: '{count} 張照片已就緒',
    cameraUsePhotos: '使用這些照片',
  },
};

export function getCameraCaptureCopy(locale: string): CameraCaptureCopy {
  return cameraCaptureCopy[normalizeLocale(locale) as SupportedLocale];
}
